import { useQuery } from '@tanstack/react-query';
import { getFeatureFlags } from '@kirin/api-client/api/feature-flags';
import { apiClient } from '@/lib/api-client';
import { retryHandlers } from './helpers';

/**
 * Response type for feature flags API
 */
export type FeatureFlagsResponse = Record<string, boolean>;

/**
 * Hook to fetch feature flags from the backend API.
 *
 * Unlike `useFeatureFlags`, this asks the **server** to evaluate the flags, so
 * the answer matches what the server-side `is*Enabled` helpers will decide for
 * the same request. Pass `organizationId` for org-scoped controls: the browser
 * Flagsmith SDK evaluates against the signed-in user's identity, which can say
 * "on" for a user whose org has the flag off, and the resulting API call then
 * fails (KIR-4096).
 *
 * Falls back gracefully when the backend is unavailable — the query does not
 * throw, and callers read `data?.[key] ?? false`.
 *
 * @param flagKeys - Array of flag keys to fetch
 * @param enabled - Whether the query should be enabled (default: true)
 * @param organizationId - Evaluate the flags for this organization
 * @returns React Query result with flag values
 *
 * @example
 * const { data: flags, isLoading } = useFeatureFlagsApi(['flag1'], true, orgId);
 * const flag1Value = flags?.flag1 ?? false;
 */
export function useFeatureFlagsApi(
  flagKeys: string[],
  enabled: boolean = true,
  organizationId?: string
): {
  data: FeatureFlagsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const flagKeysString = flagKeys.join(',');

  const queryResult = useQuery({
    queryKey: ['featureFlags', flagKeysString, organizationId ?? null],
    queryFn: async (): Promise<FeatureFlagsResponse> => {
      if (flagKeys.length === 0) {
        return {};
      }

      return await getFeatureFlags(apiClient, flagKeysString, organizationId);
    },
    enabled: enabled && flagKeys.length > 0,
    staleTime: 60 * 1000, // 60 seconds - flags don't change frequently
    retry: retryHandlers.list,
    // Don't throw errors - gracefully fallback to defaults
    throwOnError: false,
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error instanceof Error ? queryResult.error : null,
  };
}
